import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

export const useAdditionalItemGroups = ({ activeApi, token, setConfirmationModal, addAdditionalItem, removeSourceFromAdditionalItem, onGroupDeleted }) => {
  const [additionalItemGroups, setAdditionalItemGroups] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const hasFetchedData = useRef(false);

  useEffect(() => {
    if (token) {
      if (!hasFetchedData.current) {
        hasFetchedData.current = true;
        activeApi.getAdditionalItemGroups().then(setAdditionalItemGroups).catch(error => console.error("Error fetching additional item groups:", error));
      }
    } else {
      hasFetchedData.current = false;
    }
  }, [token, activeApi]);

  const addGroup = useCallback(async (group, andAnother) => {
    const isEditing = !!editingGroup;
    if (additionalItemGroups.find(g => g.title.toLowerCase() === group.title.toLowerCase() && (!isEditing || g.id !== editingGroup.id))) {
      return { error: 'A group with this name already exists.' };
    }

    const oldGroup = isEditing ? additionalItemGroups.find(g => g.id === editingGroup.id) : null;

    try {
      const savedGroup = isEditing
        ? await activeApi.updateAdditionalItemGroupAPI(editingGroup.id, group)
        : await activeApi.addAdditionalItemGroupAPI(group);

      if (isEditing) {
        if (selectedGroupIds.includes(savedGroup.id)) {
          // Remove old items from meal plan
          if (oldGroup) {
            oldGroup.items.forEach(item => {
              removeSourceFromAdditionalItem(item, `group_${oldGroup.id}`);
            });
          }
          // Add new items to meal plan
          savedGroup.items.forEach(item => {
            addAdditionalItem(item, `group_${savedGroup.id}`);
          });
        }
        setAdditionalItemGroups(prev => prev.map(g => (g.id === savedGroup.id ? savedGroup : g)));
        setEditingGroup(null);
      } else {
        setAdditionalItemGroups(prev => [...prev, savedGroup]);
      }

      if (!andAnother) {
        setIsModalOpen(false);
      }

      return { success: true, group: savedGroup };
    } catch (error) {
      return { error: error.message };
    }
  }, [additionalItemGroups, editingGroup, activeApi, selectedGroupIds, addAdditionalItem, removeSourceFromAdditionalItem]);

  const deleteGroup = useCallback((groupId) => {
    const groupToDelete = additionalItemGroups.find(g => g.id === groupId);
    const message = groupToDelete
      ? `Are you sure you want to delete item group '${groupToDelete.title}'?`
      : 'Are you sure you want to delete this group?';

    setConfirmationModal({
      isOpen: true,
      message,
      onConfirm: () => {
        activeApi.deleteAdditionalItemGroupAPI(groupId)
          .then(() => {
            const newGroups = additionalItemGroups.filter(g => g.id !== groupId);
            setAdditionalItemGroups(newGroups);
            const totalPages = Math.ceil(newGroups.length / 5);
            if (page > totalPages) {
              setPage(totalPages || 1);
            }
            if (onGroupDeleted) {
              onGroupDeleted(groupId);
            }
          })
          .catch(error => console.error('Error deleting item group:', error))
          .finally(() => setConfirmationModal({ isOpen: false, message: '', onConfirm: () => { } }));
      }
    });
  }, [activeApi, additionalItemGroups, page, setConfirmationModal, onGroupDeleted]);

  const startEditingGroup = useCallback((group) => {
    setEditingGroup(group);
    setIsModalOpen(true);
  }, []);

  const cancelEditingGroup = useCallback(() => {
    setEditingGroup(null);
    setIsModalOpen(false);
  }, []);

  const openAddGroupModal = useCallback(() => {
    setEditingGroup(null);
    setIsModalOpen(true);
  }, []);

  const handleSelection = useCallback((groupId) => {
    const group = additionalItemGroups.find(g => g.id === groupId);
    if (!group) return;

    const isSelecting = !selectedGroupIds.includes(groupId);

    if (isSelecting) {
      group.items.forEach(item => addAdditionalItem(item, `group_${groupId}`));
    } else {
      group.items.forEach(item => removeSourceFromAdditionalItem(item, `group_${groupId}`));
    }

    setSelectedGroupIds(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  }, [additionalItemGroups, selectedGroupIds, addAdditionalItem, removeSourceFromAdditionalItem]);

  const unselectAll = useCallback(() => {
    setConfirmationModal({
      isOpen: true,
      message: 'Are you sure you want to unselect all item groups? This will remove them from your meal plan.',
      onConfirm: () => {
        selectedGroupIds.forEach(groupId => {
          const group = additionalItemGroups.find(g => g.id === groupId);
          if (group) {
            group.items.forEach(item => {
              removeSourceFromAdditionalItem(item, `group_${groupId}`);
            });
          }
        });
        setSelectedGroupIds([]);
        setConfirmationModal({ isOpen: false, message: '', onConfirm: () => { } });
      }
    });
  }, [additionalItemGroups, selectedGroupIds, removeSourceFromAdditionalItem, setConfirmationModal]);

  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);
    setPage(1);
  }, []);

  const filteredGroups = useMemo(() =>
    additionalItemGroups.filter(group =>
      group && group.title && group.title.toLowerCase().includes(searchTerm.toLowerCase())
    ), [additionalItemGroups, searchTerm]
  );

  return {
    additionalItemGroups,
    filteredGroups,
    isModalOpen,
    setIsModalOpen,
    editingGroup,
    searchTerm,
    page, setPage,
    selectedGroupIds, setSelectedGroupIds,
    addGroup,
    deleteGroup,
    startEditingGroup,
    cancelEditingGroup,
    openAddGroupModal,
    handleSelection,
    unselectAll,
    handleSearchChange,
  };
};